import { Injectable } from '@angular/core';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

/**
 * Report Designer translation service
 */
@Injectable({
  providedIn: 'root'
})
export class ReportDesignerTranslatorService {
  /**
   * The current locale
   */
  private locale = 'en-US';

  /**
   * Loaded translations
   * Key is form of locale:translationKeyPrefix, f.e. en-GB:common, de-DE:common
   */
  private translations: Map<string, any> = new Map<string, any>();

  /**
   * Prevents from double-loading when called while loading
   * Key is form of locale:translationKeyPrefix, f.e. en-GB:common, de-DE:common
   */
  private loadStartedDfdMap: Map<string, Subject<any>> = new Map<string, Subject<any>>();

  /**
   * Constructor
   * @param appServiceHelper
   * @param http
   */
  constructor(private appServiceHelper: AppServiceHelperService, private http: HttpClient) { }

  /**
   * Construct a key for locale and translationKeyPrefix, f.e. en-GB:common, de-DE:common
   */
  private getLocaleTranslationKeyPrefixKey(locale: string, translationKeyPrefix: string) {
    return `${locale}:${translationKeyPrefix}`;
  }

  /**
   * Sets the ReportDesigner locale
   */
  public setLocale(locale: string): void {
    this.locale = locale;
    this.loadStartedDfdMap.clear();
  }

  /**
   * Returns the ReportDesigner locale
   */
  public getLocale(): string {
    return this.locale;
  }

  /**
   * Loads translations for current locale and resolves when loaded (or immeiatelly if already loaded)
   */
  public ready(translationKeyPrefix: string): Promise<void> {
    const mapKey = this.getLocaleTranslationKeyPrefixKey(this.locale, translationKeyPrefix);

    return new Promise((resolve, reject) => {
      let loadStartedDfd = this.loadStartedDfdMap.get(mapKey);
      if (this.translations.get(mapKey)) {
        //Already loaded - return immediately
        resolve();
      } else if (loadStartedDfd) {
        //Loading is already in progress - wait for it to terminate and return
        loadStartedDfd.pipe(take(1)).subscribe(() => {
          //Loading is done - resolve
          resolve();
        });
      }
      else {
        //Not started yet - load
        const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `widgets/translation/json/${this.locale}/${translationKeyPrefix}`);

        loadStartedDfd = new Subject<any>(); //Loading is in progress
        this.loadStartedDfdMap.set(mapKey, loadStartedDfd);

        this.http.get(url).pipe(take(1)).subscribe({
          next: (data: any) => {
            if (translationKeyPrefix === 'all') {
              for (const widgetName of Object.keys(data)) {
                this.translations.set(this.getLocaleTranslationKeyPrefixKey(this.locale, widgetName), data[widgetName]);
              }
              this.translations.set(this.getLocaleTranslationKeyPrefixKey(this.locale, 'all'), { all: 'all' });
              loadStartedDfd.next(data);
              resolve();
            }
            else {
              console.warn('Widget loaded outside of all call: ' + translationKeyPrefix);
              this.translations.set(mapKey, data);
              loadStartedDfd.next(data);
              resolve();
            }
          },
          error: (error) => {
            reject(error);
          }
        });
      }
    });
  }

  /**
   * Warnings sent for added widgets (if not translated)
   */
  private warningsSentForWidgets: string[] = [];

  /**
   * Returns translation for current locale and tranlation key, translation key is f.e. keyFigures.mainTenant
   */
  public getString(translationKey: string): string {
    const firstDotIndex = translationKey.indexOf('.');
    const lastDotIndex = translationKey.lastIndexOf('.');
    if (firstDotIndex < 0) {
      console.warn('Invalid translation key: ' + translationKey);
      return '';
    }
    if (firstDotIndex !== lastDotIndex) {
      console.warn('Invalid translation key: ' + translationKey);
      return '';
    }
    const translationKeyPrefix = translationKey.substring(0, firstDotIndex);
    const translationKeySuffix = translationKey.substring(firstDotIndex + 1);

    const mapKey = this.getLocaleTranslationKeyPrefixKey(this.locale, translationKeyPrefix);

    if (!this.translations.has(mapKey)) {
      if (this.warningsSentForWidgets.indexOf(translationKeyPrefix) < 0) {
        this.warningsSentForWidgets.push(translationKeyPrefix);

        console.warn('Translation key prefix not found / widget not translated or supported: ' + translationKeyPrefix);
        alert('Unsupported widget detected: ' + translationKeyPrefix + '. Please remove this widget');
      }
      return '';
    }
    const translations = this.translations.get(mapKey);
    if (translations[translationKeySuffix]) {
      return translations[translationKeySuffix];
    }
    console.error('Translation key not found: Widget is translated, but this field is missing: ' + translationKey);
    return '';
  }
}
